import { useCallback, useState } from 'react';

/**
 * JSON シリアライズ可能な値を localStorage に永続化する hook。
 * 復元時のバリデーション（不正な保存値の救済）は `validate` で行う。
 */
export function usePersistedJSON<T>(
  key: string,
  defaultValue: T,
  validate: (parsed: unknown) => parsed is T,
) {
  const [value, setValue] = useState<T>(() => {
    try {
      const stored = localStorage.getItem(key);
      if (stored === null) return defaultValue;
      const parsed = JSON.parse(stored) as unknown;
      return validate(parsed) ? parsed : defaultValue;
    } catch {
      return defaultValue;
    }
  });

  const set = useCallback(
    (updater: T | ((prev: T) => T)) => {
      setValue((prev) => {
        const next = typeof updater === 'function' ? (updater as (p: T) => T)(prev) : updater;
        try {
          localStorage.setItem(key, JSON.stringify(next));
        } catch {
          // ignore storage errors (quota / disabled)
        }
        return next;
      });
    },
    [key],
  );

  return [value, set] as const;
}
